import { CalendarEventTypesFragment, ContentBlockFragment } from '@graphqlTypes/sitecore';
import { sortContentBlocksByEventDate } from '@src/common/scores/sort-contents';
import { RacingScheduleItem } from '@src/utils/js-bridge/js-bridge';
import { DeepReadonly } from '@src/utils/type-utils';
import {
    addWeeks,
    format,
    isSameMonth,
    parse,
    startOfMonth,
    subWeeks,
    startOfWeek,
} from 'date-fns';
import { makeAutoObservable } from 'mobx';
import { AppBaseStore } from './app-base-store';
import { SettingStore } from './setting-store';

export class CalendarStore {
    selectedEventType = '';
    selectedDate: Date | undefined = new Date();
    currentWeek = startOfWeek(Date.now());
    currentMonth = startOfMonth(Date.now());
    isOneWeekMode = true;
    constructor(private baseStore: AppBaseStore, private settingStore: SettingStore) {
        makeAutoObservable(this, undefined, { deep: false });
    }

    get filteredContentBlocks(): Record<string, ReadonlyArray<ContentBlockWithSection>> {
        if (!this.selectedEventType) {
            return { ...this.contentBlocks };
        }
        const reslut: Record<string, ReadonlyArray<ContentBlockWithSection>> = {};
        Object.entries(this.contentBlocks).forEach(([key, contentblocks]) => {
            reslut[key] = contentblocks.filter(
                (block) => block.eventType?.targetItem?.key === this.selectedEventType
            );
        });
        return reslut;
    }

    setSelectedEventType(eventType: string) {
        this.selectedEventType = eventType;
    }
    setSelectedDate(date?: Date) {
        this.selectedDate = date;
    }

    get contentBlocks(): Record<string, ReadonlyArray<ContentBlockWithSection>> {
        const eventItems: DeepReadonly<ContentBlockWithSection>[] = [];

        //events from sitecore
        this.baseStore.data?.mainContents?.Item?.sections.forEach((section) => {
            const contentBlocks = section.contentBlocks.filter((b) => b.calendarEnabled?.boolValue);
            const blocksWithSection = contentBlocks.map((b) => withSectionPath(section.key, b));
            eventItems.push(...blocksWithSection);
        });

        //racingEventType for live schedule
        const racingEventType = this.baseStore.data?.eventTypes.find(
            (value) => value.key === 'racing'
        );
        //racingEventType for MsLiveSchedule
        const racingMsEventType = this.baseStore.data?.eventTypes.find(
            (value) => value.key === 'marksix'
        );

        if (this.settingStore.liveSchedule) {
            this.settingStore.liveSchedule?.racingScheduleList.forEach((sche) => {
                if (sche.date) {
                    const startTime = parse(
                        `${sche.date} ${sche.time}`,
                        'dd/MM/yyyy HH:mm',
                        new Date()
                    );
                    const endTime = parse(
                        `${sche.endDate} ${sche.endTime}`,
                        'dd/MM/yyyy HH:mm',
                        new Date()
                    );
                    const content: ContentBlockWithSection = {
                        key: `${format(startTime, 'yyyyMMddHHmm')}`, //use start date time as key
                        section: 'liveSchedule',
                        calendarEnabled: { boolValue: true },
                        liveSchedule: sche,
                        startDatetime: { dateValue: startTime.getTime() },
                        endDatetime: { dateValue: endTime.getTime() },
                        eventDate: { dateValue: startTime.getTime() },
                        calendarTitle: { value: sche.title },
                        eventType: { targetItem: racingEventType },
                    };
                    eventItems.push(content);
                }
            });
        }

        if (this.settingStore.MsLiveSchedule) {
            this.settingStore.MsLiveSchedule?.msScheduleList.forEach((sche) => {
                if (sche.date) {
                    const startTime = parse(
                        `${sche.date} ${sche.time}`,
                        'dd/MM/yyyy HH:mm',
                        new Date()
                    );
                    const endTime = parse(
                        `${sche.endDate} ${sche.endTime}`,
                        'dd/MM/yyyy HH:mm',
                        new Date()
                    );
                    const content: ContentBlockWithSection = {
                        key: `${format(startTime, 'yyyyMMddHHmm')}`, //use start date time as key
                        section: 'MsLiveSchedule',
                        calendarEnabled: { boolValue: true },
                        MsLiveSchedule: sche,
                        startDatetime: { dateValue: startTime.getTime() },
                        endDatetime: { dateValue: endTime.getTime() },
                        eventDate: { dateValue: startTime.getTime() },
                        calendarTitle: { value: sche.title },
                        eventType: { targetItem: racingMsEventType },
                    };
                    eventItems.push(content);
                }
            });
        }

        sortContentBlocksByEventDate(eventItems);
        const formatedEventItems = formatContentBlocksByDate(eventItems); //format events  by date
        return formatedEventItems;
    }
    prevWeek() {
        this.currentWeek = subWeeks(this.currentWeek, 1);
    }
    nextWeek() {
        this.currentWeek = addWeeks(this.currentWeek, 1);
    }
    setCurrentWeek(week: Date) {
        this.currentWeek = startOfWeek(week);
    }
    setCurrentMonth(month: Date) {
        this.currentMonth = startOfMonth(month);
    }
    toggleOneWeekMode() {
        this.isOneWeekMode = !this.isOneWeekMode;
        if (this.isOneWeekMode) {
            if (this.selectedDate && isSameMonth(this.currentMonth, this.selectedDate)) {
                this.currentWeek = startOfWeek(this.selectedDate);
            } else if (this.selectedDate && isSameMonth(this.currentMonth, Date.now())) {
                this.currentWeek = startOfWeek(Date.now());
            } else {
                this.currentWeek = startOfWeek(startOfMonth(this.currentMonth));
            }
        }
    }
    setDefaults() {
        const now = new Date();
        this.selectedEventType = '';
        this.selectedDate = now;
        this.currentWeek = startOfWeek(now);
        this.currentMonth = startOfMonth(now);
        this.isOneWeekMode = true;
    }

    get eventTypes() {
        return this.baseStore.data?.eventTypes;
    }
}

function formatContentBlocksByDate(contentBlocks?: ContentBlockWithSection[]) {
    const records = {} as Record<string, ContentBlockWithSection[]>;
    contentBlocks?.forEach((current) => {
        if (current.eventDate?.dateValue) {
            const date = format(current.eventDate.dateValue, 'yyyy/MM/dd');

            if (!records[date]) {
                records[date] = [current];
            } else {
                records[date].push(current);
            }
        }
    });

    return records;
}

/**
 * this function will add the key of section to content block
 * @param sectionPath key of section
 */
export function withSectionPath(
    sectionPath: string,
    contentBlock: DeepReadonly<ContentBlockFragment>
): DeepReadonly<ContentBlockWithSection> {
    sectionPath ??= '';
    return { section: sectionPath, ...contentBlock };
}

export type ContentBlockWithSection = {
    url?: string;
    id?: string;
    section: string;
    liveSchedule?: RacingScheduleItem;
    MsLiveSchedule?: RacingScheduleItem;
    key: string;
    pageTitle?: { value?: string };
    // category?: CategoriesMultilistFieldFragment;
    // hashTags?: HashTagsMultilistFieldFragment;
    // segmentCodes?: SegmentCodeMultilistFieldFragment;
    // accountListCodes?: AccountListMultilistFieldFragment;
    isPublished?: { boolValue?: boolean };
    startDatetime?: { dateValue?: number };
    endDatetime?: { dateValue?: number };
    editorsPick?: { boolValue?: boolean };
    eventType?: { targetItem?: CalendarEventTypesFragment };
    eventDate?: { dateValue?: number };
    eventEndDate?: { dateValue?: number };
    calendarTitle?: { value?: string };
    calendarSubTitle?: { value?: string };
    calendarEnabled?: { boolValue?: boolean };
    calendarActionText?: { value?: string };
    addToCalendarEnabled?: { boolValue?: boolean };
    videoTitle?: { value?: string };
    videoLink?: { value?: string };
    videoPoster?: { src?: string };
    videoNative?: { boolValue?: boolean };
    videoNativePip?: { boolValue?: boolean };
    blockType?: { value?: string };
    externalHashId?: { boolValue?: boolean };
    externalAc?: { boolValue?: boolean };
    externalSeg?: { boolValue?: boolean };
    externalLink?: { value?: string };
    internalLink?: { value?: string };
    deepLink?: { value?: string };
    raceLink?: {
        targetItem?: { path: string } | { path: string } | { path: string } | { path: string };
    };
};
